// src/App.js
import React, { useState, useEffect } from "react";
import "./App.css";
import { Document, Packer, Paragraph, TextRun, Table, TableCell, TableRow, WidthType } from "docx";
import { saveAs } from "file-saver";

function App() {
  const [activeTab, setActiveTab] = useState("sesiones");

  useEffect(() => {
    console.log("API URL:", process.env.REACT_APP_API_URL);
  }, []);

  // Estado completo para sesiones
  const [sesionData, setSesionData] = useState({
    institucion: "",
    area: "",
    gradoSeccion: "",
    docente: "",
    fecha: "",
    duracion: "",
    tema: "",
    indicaciones: ""
  });

  const [status, setStatus] = useState("");
  const [errors, setErrors] = useState({});

  const handleSesionChange = (e) => {
    const { name, value } = e.target;
    setSesionData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validación de tiempo
  const validateTime = (data) => {
    const duracion = parseInt(data.duracion);
    if (isNaN(duracion) || duracion <= 0) return "Duración debe ser un número positivo.";
    // Si hay momentos, validar suma (asumiendo que AI los genera)
    return null;
  };

  // Mapeo completo de respuesta IA
  const mapAIToTemplate = (userData, aiData) => {
    return {
      titulo: aiData.titulo || `Sesión: ${userData.tema}`,
      institucion: userData.institucion,
      area: userData.area,
      gradoSeccion: userData.gradoSeccion,
      docente: userData.docente,
      fecha: userData.fecha,
      duracion: userData.duracion,
      tema: userData.tema,
      indicaciones: userData.indicaciones,
      proposito: aiData.proposito || "",
      competencias: aiData.competencias ? aiData.competencias.map(c => `${c.competencia}: ${c.capacidades.join(', ')}`) : [],
      desempenos: aiData.desempenos || [],
      evidencias: aiData.evidencias || "",
      instrumentos: aiData.instrumentos || "",
      competenciasTransversales: aiData.competenciasTransversales || [],
      enfoque: aiData.enfoque || "",
      acciones: aiData.acciones || "",
      momentos: aiData.momentos || {},
      recursos: aiData.recursos || [],
      materiales: aiData.materiales || []
    };
  };

  // Generación DOCX avanzada
  const generarDocxDesdeTemplate = async (data, filename) => {
    const children = [
      new Paragraph({ children: [new TextRun({ text: data.titulo, bold: true, size: 32 })] }),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "INFORMACIÓN GENERAL", bold: true, underline: {} })] }),
      new Paragraph(`Institución: ${data.institucion}`),
      new Paragraph(`Área Curricular: ${data.area}`),
      new Paragraph(`Grado y Sección: ${data.gradoSeccion}`),
      new Paragraph(`Docente: ${data.docente}`),
      new Paragraph(`Fecha: ${data.fecha}`),
      new Paragraph(`Duración Total: ${data.duracion} minutos`),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "I. TEMA", bold: true })] }),
      new Paragraph(data.tema),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "II. PROPÓSITO DE APRENDIZAJE", bold: true })] }),
      new Paragraph(data.proposito),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "III. COMPETENCIAS", bold: true })] }),
      ...data.competencias.map((c) => new Paragraph({ text: `• ${c}`, indent: { left: 720 } })),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "IV. DESEMPEÑOS", bold: true })] }),
      ...data.desempenos.map((d) => new Paragraph({ text: `• ${d}`, indent: { left: 720 } })),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "V. EVIDENCIAS", bold: true })] }),
      new Paragraph(data.evidencias),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "VI. INSTRUMENTOS DE EVALUACIÓN", bold: true })] }),
      new Paragraph(data.instrumentos),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "VII. COMPETENCIAS TRANSVERSALES", bold: true })] }),
      ...data.competenciasTransversales.map((ct) => new Paragraph({ text: `• ${ct}`, indent: { left: 720 } })),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "VIII. ENFOQUE METODOLÓGICO", bold: true })] }),
      new Paragraph(data.enfoque),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "IX. ACCIONES PEDAGÓGICAS", bold: true })] }),
      new Paragraph(data.acciones),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "X. DESARROLLO DE LA SESIÓN", bold: true })] }),
      // Tabla para momentos
      ...(data.momentos.inicio || data.momentos.desarrollo || data.momentos.cierre ? [
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({
              children: [
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Momento", bold: true })] })] }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Tiempo", bold: true })] })] }),
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Actividades", bold: true })] })] })
              ]
            }),
            ...(data.momentos.inicio ? [new TableRow({
              children: [
                new TableCell({ children: [new Paragraph("Inicio")] }),
                new TableCell({ children: [new Paragraph(data.momentos.inicio.tiempo)] }),
                new TableCell({ children: [new Paragraph(data.momentos.inicio.actividades)] })
              ]
            })] : []),
            ...(data.momentos.desarrollo ? [new TableRow({
              children: [
                new TableCell({ children: [new Paragraph("Desarrollo")] }),
                new TableCell({ children: [new Paragraph(data.momentos.desarrollo.tiempo)] }),
                new TableCell({ children: [new Paragraph(data.momentos.desarrollo.actividades)] })
              ]
            })] : []),
            ...(data.momentos.cierre ? [new TableRow({
              children: [
                new TableCell({ children: [new Paragraph("Cierre")] }),
                new TableCell({ children: [new Paragraph(data.momentos.cierre.tiempo)] }),
                new TableCell({ children: [new Paragraph(data.momentos.cierre.actividades)] })
              ]
            })] : [])
          ]
        }),
        new Paragraph(" ")
      ] : []),
      new Paragraph({ children: [new TextRun({ text: "XI. RECURSOS", bold: true })] }),
      ...data.recursos.map((r) => new Paragraph({ text: `• ${r}`, indent: { left: 720 } })),
      new Paragraph(" "),
      new Paragraph({ children: [new TextRun({ text: "XII. MATERIALES", bold: true })] }),
      ...data.materiales.map((m) => new Paragraph({ text: `• ${m}`, indent: { left: 720 } }))
    ];

    const doc = new Document({
      sections: [{ children }]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, filename);
  };

  // Manejo del envío
  const handleSesionSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    setErrors({});

    // Validaciones
    const timeError = validateTime(sesionData);
    if (timeError) {
      setErrors({ duracion: timeError });
      return;
    }

    if (!sesionData.tema.trim()) {
      setErrors({ tema: "Tema es obligatorio." });
      return;
    }

    setStatus("⏳ Generando sesión con IA...");

    const workerBase = process.env.REACT_APP_API_URL || "https://backend-sesiones.escobar.workers.dev";
    const endpoint = `${workerBase.replace(/\/$/, "")}/api/generar`;

    try {
      const resp = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sesionData),
      });

      let json;
      try {
        json = await resp.json();
      } catch (parseErr) {
        // If not JSON, get text
        const text = await resp.text();
        throw new Error(`Respuesta no JSON: ${text}`);
      }

      if (!resp.ok || !json.ok) {
        const errorMsg = json.error ? (typeof json.error === 'string' ? json.error : JSON.stringify(json.error)) : "Error en la API";
        throw new Error(errorMsg);
      }

      const aiResult = json.data;
      const mapped = mapAIToTemplate(sesionData, aiResult);

      const filename = `${mapped.titulo.replace(/[^a-z0-9_-]/gi, "_")}.docx`;
      await generarDocxDesdeTemplate(mapped, filename);

      setStatus("✅ Documento generado exitosamente. Descarga iniciada.");
    } catch (err) {
      console.error(err);
      const errorMsg = err.message || err.toString();
      setStatus(`❌ Error: ${errorMsg}`);
    }
  };

  return (
    <div className="app-container">
      <nav className="navbar">
        <ul>
          <li><button type="button" className="nav-link">Inicio</button></li>
          <li><button type="button" className="nav-link">Acerca</button></li>
          <li><button type="button" className="nav-link">Contacto</button></li>
        </ul>
      </nav>

      <header className="header">
        <img src="/logo192.png" alt="Logo" className="logo" />
        <h1>Generador de Sesiones de Aprendizaje</h1>
        <p>Con IA y formato profesional en DOCX</p>
      </header>

      <div className="tabs">
        <button
          className={activeTab === "sesiones" ? "tab active" : "tab"}
          onClick={() => setActiveTab("sesiones")}
        >
          Sesiones
        </button>
        <button
          className={activeTab === "unidades" ? "tab active" : "tab"}
          onClick={() => setActiveTab("unidades")}
        >
          Unidades
        </button>
        <button
          className={activeTab === "plan" ? "tab active" : "tab"}
          onClick={() => setActiveTab("plan")}
        >
          Plan Anual
        </button>
      </div>

      <div className="form-container">
        {activeTab === "sesiones" && (
          <form onSubmit={handleSesionSubmit}>
            <h2>Generar Sesión de Aprendizaje</h2>

            <fieldset>
              <legend>Información del Docente</legend>
              <label>Institución Educativa</label>
              <input
                type="text"
                name="institucion"
                value={sesionData.institucion}
                onChange={handleSesionChange}
                required
              />

              <label>Área Curricular</label>
              <input
                type="text"
                name="area"
                value={sesionData.area}
                onChange={handleSesionChange}
                required
              />

              <label>Grado y Sección</label>
              <input
                type="text"
                name="gradoSeccion"
                value={sesionData.gradoSeccion}
                onChange={handleSesionChange}
                required
              />

              <label>Docente</label>
              <input
                type="text"
                name="docente"
                value={sesionData.docente}
                onChange={handleSesionChange}
                required
              />

              <label>Fecha</label>
              <input
                type="date"
                name="fecha"
                value={sesionData.fecha}
                onChange={handleSesionChange}
                required
              />

              <label>Duración Total (minutos)</label>
              <input
                type="number"
                name="duracion"
                value={sesionData.duracion}
                onChange={handleSesionChange}
                min="1"
                placeholder="ej. 60"
                required
              />
              {errors.duracion && <span className="error">{errors.duracion}</span>}
            </fieldset>

            <fieldset>
              <legend>Contenido de la Sesión</legend>
              <label>Tema</label>
              <input
                type="text"
                name="tema"
                value={sesionData.tema}
                onChange={handleSesionChange}
                required
              />
              {errors.tema && <span className="error">{errors.tema}</span>}

              <label>Indicaciones Adicionales</label>
              <textarea
                name="indicaciones"
                value={sesionData.indicaciones}
                onChange={handleSesionChange}
                rows={4}
                placeholder="Instrucciones especiales para la generación..."
              />
            </fieldset>

            <button type="submit" className="primary">Generar Sesión</button>
          </form>
        )}

        {status && (
          <div className={`status-box ${status.includes("❌") ? "error" : "success"}`}>
            <p>{status}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
